// apps/client/src/components/clientSets/CreateClientSetDialog.tsx
import React, { useState, useMemo } from 'react';
import { useCreateClientSet } from '../../hooks/useCreateClientSet';
import { useCustomerSearch } from '../../hooks/useCustomerSearch';
import type { CustomerSearchResult } from '../../hooks/useCustomerSearch';

interface CreateClientSetDialogProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateClientSetDialog: React.FC<CreateClientSetDialogProps> = ({
  onClose,
  onSuccess,
}) => {
  const [clientSetName, setClientSetName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState<CustomerSearchResult[]>([]);

  // Memoize to prevent new array reference on every render
  const excludeCustomers = useMemo(
    () => selectedCustomers.map(c => c.customerName),
    [selectedCustomers]
  );

  const { createClientSet, loading: creating, error: createError } = useCreateClientSet();
  const { customers, loading: searching } = useCustomerSearch({
    searchTerm,
    excludeCustomers,
    enabled: searchTerm.length >= 2,
  });

  const handleAddCustomer = (customer: CustomerSearchResult) => {
    setSelectedCustomers([...selectedCustomers, customer]);
    setSearchTerm('');
  };

  const handleRemoveCustomer = (customerName: string) => {
    setSelectedCustomers(selectedCustomers.filter(c => c.customerName !== customerName));
  };

  const handleCreate = async () => {
    if (!clientSetName.trim() || selectedCustomers.length < 2) {
      return;
    }

    try {
      await createClientSet({
        clientSetName: clientSetName.trim(),
        customerNames: selectedCustomers.map(c => c.customerName),
      });
      onSuccess();
    } catch (err) {
      // Error is handled by the hook
      console.error('Failed to create client set:', err);
    }
  };

  const totalLoans = selectedCustomers.reduce((sum, c) => sum + c.loanCount, 0);
  const totalValue = selectedCustomers.reduce((sum, c) => sum + c.totalLoanValue, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800 z-10">
          <h2 className="text-2xl font-bold text-white">Create Client Group</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Client Set Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Client Group Name
            </label>
            <input
              type="text"
              value={clientSetName}
              onChange={(e) => setClientSetName(e.target.value)}
              placeholder="e.g., Smith Family Group"
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Search Customers */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Search & Add Customers
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search customer names..."
                className="w-full px-4 py-2 pl-10 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <svg
                className="absolute left-3 top-3 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Search Results */}
            {searchTerm.length >= 2 && (
              <div className="mt-2 border border-gray-700 rounded-lg max-h-48 overflow-y-auto">
                {searching ? (
                  <div className="p-4 text-center text-gray-400">Searching...</div>
                ) : customers.length === 0 ? (
                  <div className="p-4 text-center text-gray-400">No customers found</div>
                ) : (
                  customers.map((customer) => (
                    <div
                      key={customer.customerName}
                      className="p-3 hover:bg-gray-700 cursor-pointer flex justify-between items-center border-b border-gray-700 last:border-b-0"
                      onClick={() => handleAddCustomer(customer)}
                    >
                      <div>
                        <div className="font-medium text-white">{customer.customerName}</div>
                        <div className="text-sm text-gray-400">
                          {customer.loanCount} loans • ${customer.totalLoanValue.toLocaleString()}
                        </div>
                      </div>
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Selected Customers */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Selected Customers ({selectedCustomers.length})
            </label>
            {selectedCustomers.length === 0 ? (
              <div className="text-gray-500 text-center py-8 border border-gray-700 rounded-lg border-dashed">
                Search and add at least 2 customers to create a client group
              </div>
            ) : (
              <div className="space-y-2">
                {selectedCustomers.map((customer) => (
                  <div
                    key={customer.customerName}
                    className="flex justify-between items-center p-3 border border-gray-700 rounded-lg bg-gray-900"
                  >
                    <div>
                      <div className="font-medium text-white">{customer.customerName}</div>
                      <div className="text-sm text-gray-400">{customer.loanCount} loans</div>
                    </div>
                    <button
                      onClick={() => handleRemoveCustomer(customer.customerName)}
                      className="text-red-500 hover:text-red-400 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          {selectedCustomers.length > 0 && (
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-2">Summary</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Total Loans:</span>
                  <span className="ml-2 text-white font-medium">{totalLoans}</span>
                </div>
                <div>
                  <span className="text-gray-400">Total Value:</span>
                  <span className="ml-2 text-white font-medium">${totalValue.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {createError && (
            <div className="bg-red-900/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
              {createError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3 sticky bottom-0 bg-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!clientSetName.trim() || selectedCustomers.length < 2 || creating}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? 'Creating...' : 'Create Client Group'}
          </button>
        </div>
      </div>
    </div>
  );
};
